import { useConfiguratorProductCode } from "~/Configurators/common/useConfiguratorType";
import {
  ConfiguratorType,
  getConfiguratorFamilyFromProductCode,
  getConfiguratorTypeFromProductCode,
} from "~/Configurators/ConfiguratorType";
import { useTranslation } from "react-i18next";
import { type TFunction, type WithT } from "i18next";
import { useSelector } from "react-redux";
import { selectAllSteps } from "~/Configurators/Linear/redux/selectors";

type ReturnType = {
  t: TFunction;
  tPrefix: string;
  renderStepName: (index: number) => string;
};

type UndefinedReturnType = Record<keyof ReturnType, undefined>;
export function useXmlConfiguratorTranslations(): ReturnType | UndefinedReturnType {
  const productCode = useConfiguratorProductCode();
  const configuratorType = getConfiguratorTypeFromProductCode(productCode);
  const config = XML_TRANSLATION_SHEETS[configuratorType ?? ""];
  if (!config) {
    return {
      t: undefined,
      tPrefix: undefined,
      renderStepName: undefined,
    };
  }
  return useForcedConfiguratorTranslations(productCode);
}

export function useForcedConfiguratorTranslations(productCode: string): ReturnType | UndefinedReturnType {
  const configuratorType = getConfiguratorTypeFromProductCode(productCode);
  const configuratorFamily = getConfiguratorFamilyFromProductCode(productCode);
  const config = XML_TRANSLATION_SHEETS[configuratorType ?? ""];
  if (!configuratorType || !config) {
    return {
      t: undefined,
      tPrefix: undefined,
      renderStepName: undefined,
    };
  }
  const { t: originalT } = useTranslation(config?.sheetName);
  const steps = useSelector(selectAllSteps);
  const t = ((key: string, options?: object) =>
    originalT(key, { context: configuratorFamily, ...options })) as WithT["t"];
  return {
    t,
    tPrefix: config?.tPrefix,
    renderStepName: (index: number) => {
      return t(steps[index].name);
    },
  };
}

export const XML_TRANSLATION_SHEETS = {
  [ConfiguratorType.System]: {
    sheetName: "unitConfigurator",
    tPrefix: "configurator.unit",
  },
  [ConfiguratorType.Rail]: {
    sheetName: "railConfigurator",
    tPrefix: "configurator.rail",
  },
  [ConfiguratorType.Carriage]: {
    sheetName: "carriageConfigurator",
    tPrefix: "configurator.carriage",
  },
  [ConfiguratorType.MetricShaft]: {
    sheetName: "shaftConfigurator",
    tPrefix: "configurator.shaft",
  },
  [ConfiguratorType.InchShaft]: {
    sheetName: "shaftConfigurator",
    tPrefix: "configurator.shaft",
  },
};
